import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppState } from "../store";
import {
  NotificationBanner,
  NotificationType,
  OnboardingFlowType,
} from "@markit/common.types";

// Used to track the current onboarding step the user is on.
export type CurrentTourStep = {
  flowType: OnboardingFlowType;
  stepNumber: number;
  totalSteps?: number; // undefined when the total isn't known yet (the first step, before a flow is chosen)
  isStepVisible: boolean;
  hasBeenVisible: boolean;
  hideStepIndicator: boolean;
};

export type NotificationState = {
  notificationBanner: NotificationBanner;
  markitPlusModal: string | null;
  accountMismatchModal: boolean; // Modal to show on other tabs when user switches accounts on one tab
  currentTourStep: CurrentTourStep | null;
};

export const initialNotificationState: NotificationState = {
  notificationBanner: {
    lastTriggered: "",
    confirmAction: false,
    title: "",
    type: NotificationType.NONE,
    altIcon: "",
  },
  markitPlusModal: null,
  accountMismatchModal: false,
  currentTourStep: null,
};

export const notificationSlice = createSlice({
  name: "notification",
  initialState: initialNotificationState,
  reducers: {
    setNotificationBanner: (
      state,
      action: PayloadAction<NotificationBanner>
    ) => {
      state.notificationBanner = action.payload;
    },
    clearNotificationBanner: (state) => {
      state.notificationBanner = {
        lastTriggered: state.notificationBanner.lastTriggered,
        confirmAction: false,
        title: "",
        type: NotificationType.NONE,
        altIcon: "",
      };
    },
    setMarkitPlusModal: (state, action: PayloadAction<string | null>) => {
      state.markitPlusModal = action.payload;
    },
    setAccountMismatchModal: (state, action: PayloadAction<boolean>) => {
      state.accountMismatchModal = action.payload;
    },
    setCurrentTourStep: (
      state,
      action: PayloadAction<
        Omit<CurrentTourStep, "isStepVisible" | "hasBeenVisible">
      >
    ) => {
      const { flowType, stepNumber, totalSteps, hideStepIndicator } =
        action.payload;
      if (
        !state.currentTourStep ||
        state.currentTourStep.flowType !== flowType ||
        stepNumber >= state.currentTourStep.stepNumber
      ) {
        state.currentTourStep = {
          flowType,
          stepNumber,
          totalSteps,
          isStepVisible: false,
          hasBeenVisible: false,
          hideStepIndicator,
        };
      }
    },
    setTourStepVisible: (
      state,
      action: PayloadAction<{
        flowType: OnboardingFlowType;
        stepNumber: number;
        visible: boolean;
      }>
    ) => {
      const { flowType, stepNumber, visible } = action.payload;
      if (
        state.currentTourStep?.flowType === flowType &&
        state.currentTourStep.stepNumber === stepNumber
      ) {
        state.currentTourStep.isStepVisible = visible;
        if (visible) {
          state.currentTourStep.hasBeenVisible = true;
        }
      }
    },
    clearCurrentTourStep: (
      state,
      action: PayloadAction<{ flowType: OnboardingFlowType }>
    ) => {
      if (state.currentTourStep?.flowType === action.payload.flowType) {
        state.currentTourStep = null;
      }
    },
  },
});

export const notificationActions = notificationSlice.actions;
export const notificationReducer = notificationSlice.reducer;

export const getNotificationState = (state: AppState) => state.notifications;
